import React, { useState, useEffect } from "react";
import ProgressBar from "./ProgressBar";
import HotelFormStep1AA from "./HotelFormStep1AA";
import HotelFormStep1A from "./HotelFormStep1A";
import HotelFormStep1B from "./HotelFormStep1B";
import HotelFormStep2 from "./HotelFormStep2";
import HotelFormStep5Registration from "./HotelFormStep5Registration";
import axiosInstance from "../api/axiosInstance";
import { useNavigate } from "react-router-dom";
import "./HotelFormNew.css";

// helper used by submit
const normalizeWebsite = (w) => {
  if (!w || !w.trim()) return null;
  const s = w.trim();
  return /^https?:\/\//i.test(s) ? s : `https://${s}`;
};

export default function HotelForm() {
  const [step, setStep] = useState(1);
  const steps = ["Property Type", "Basic Info", "Location", "Amenities", "Owner"];

  const [hotelData, setHotelData] = useState({
    HotelName: "",
    Address: "",
    ContactNumber: "",
    CountryDialCode: "+267",
    PhysicalLocation: "",
    TownOrVillage: "",
    District: "",
    Province: "",
    Country: "",
    Email: "",
    Website: "",
    SocialMediaHandle: "",
    MapCoordinates: "",
    NumberOfRooms: 0,
    Type: "",
    Amenities: [],
    Activities: [],
    Description: "",
    Events: []
  });

  const [userData, setUserData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    username: "",
    password: "",
    confirmPassword: ""
  });

  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [canProceedStep1, setCanProceedStep1] = useState(false);
  const navigate = useNavigate();

  // Auto-set username from email
  useEffect(() => {
    if (userData.email) {
      setUserData((prev) => ({ ...prev, username: userData.email }));
    }
  }, [userData.email]);

  // Validators
  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const validateWebsite = (website) =>
    /^(https?:\/\/)?(www\.)?[\w-]+\.[\w-]+(\/.*)?$/.test(website);
  const validatePhoneNumber = (number, dialCode) => {
    const fullNumber = dialCode + number;
    return /^\+?[\d\s-]{8,}$/.test(fullNumber);
  };
  const validatePassword = (password) => password.length >= 8;

  const validateStepFields = () => {
    const newErrors = {};

    if (step === 1 && !hotelData.Type) {
      newErrors.Type = "Property type is required";
    }

    if (step === 2) {
      if (!hotelData.HotelName.trim()) newErrors.HotelName = "Property name is required";
      if (!hotelData.Address.trim()) newErrors.Address = "Address is required";

      if (!hotelData.ContactNumber.trim()) {
        newErrors.ContactNumber = "Contact number is required";
      } else if (!validatePhoneNumber(hotelData.ContactNumber, hotelData.CountryDialCode)) {
        newErrors.ContactNumber = "Invalid phone number format";
      }

      if (!hotelData.PhysicalLocation.trim()) newErrors.PhysicalLocation = "Physical location is required";
      if (!hotelData.TownOrVillage.trim()) newErrors.TownOrVillage = "Town/Village is required";
    }

    if (step === 3) {
      if (!hotelData.District.trim()) newErrors.District = "District is required";
      if (!hotelData.Province.trim()) newErrors.Province = "Province is required";
      if (!hotelData.Country.trim()) newErrors.Country = "Country is required";

      if (!hotelData.Email.trim()) {
        newErrors.Email = "Email is required";
      } else if (!validateEmail(hotelData.Email)) {
        newErrors.Email = "Invalid email format";
      }

      // optional website
      if (hotelData.Website && !validateWebsite(hotelData.Website)) {
        newErrors.Website = "Invalid website URL";
      }
    }

    if (step === 5) {
      if (!userData.firstName.trim()) newErrors.firstName = "First name is required";
      if (!userData.lastName.trim()) newErrors.lastName = "Last name is required";

      if (!userData.email.trim()) {
        newErrors.email = "Email is required";
      } else if (!validateEmail(userData.email)) {
        newErrors.email = "Invalid email format";
      }

      if (!userData.password) {
        newErrors.password = "Password is required";
      } else if (!validatePassword(userData.password)) {
        newErrors.password = "Password must be at least 8 characters";
      }

      if (userData.password !== userData.confirmPassword) {
        newErrors.confirmPassword = "Passwords do not match";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNextStep = () => {
    if (validateStepFields()) setStep((prev) => prev + 1);
  };
  const handlePreviousStep = () => setStep((prev) => prev - 1);
  const handleStepClick = (index) => {
    if (index + 1 <= step) setStep(index + 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStepFields()) return;

    try {
      setIsSubmitting(true);

      const hotelPayload = {
        ...hotelData,
        Website: normalizeWebsite(hotelData.Website),
        NumberOfRooms:
          hotelData.NumberOfRooms == null || hotelData.NumberOfRooms === ""
            ? null
            : Number(hotelData.NumberOfRooms),
      };
      if (hotelPayload.Website == null) delete hotelPayload.Website;

      const payload = {
        hotel: hotelPayload,
        user: {
          firstName: userData.firstName,
          lastName: userData.lastName,
          email: userData.email,
          username: userData.username,
          password: userData.password,
          role: "Owner",
        },
      };

      const res = await axiosInstance.post("/User/RegisterWithHotel", payload);

      if (res.status === 201) {
        setMessage("Registration successful! Please check your email for confirmation.");
        setTimeout(() => navigate("/login"), 5000);
      }
    } catch (err) {
      console.error("API Error", err.response?.data || err.message);

      if (err.response?.data?.errors) {
        const apiErrors = {};
        Object.keys(err.response.data.errors).forEach((key) => {
          const formField = key.replace(/^Hotel\./, "");
          apiErrors[formField] = err.response.data.errors[key][0];
        });
        setErrors(apiErrors);
        setError("Please fix the validation errors below.");

        if (apiErrors.HotelName || apiErrors.Address || apiErrors.ContactNumber) {
          setStep(2);
        } else if (apiErrors.District || apiErrors.Province || apiErrors.Email || apiErrors.Website) {
          setStep(3);
        }
      } else {
        setError(err.response?.data?.message || "An error occurred during registration.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="form-wizard"
      style={{
        backgroundImage: `url('/images/registration-background.png')`,
        backgroundSize: "cover",
        backgroundPosition: "center center",
      }}
    >
      <ProgressBar steps={steps} currentStep={step} setStep={handleStepClick} />

      <div className="form-container">
        {step === 1 && (
          <HotelFormStep1AA
            hotelData={hotelData}
            setHotelData={setHotelData}
            setCanProceedStep1={setCanProceedStep1}
            errors={errors}
          />
        )}
        {step === 2 && (
          <HotelFormStep1A hotelData={hotelData} setHotelData={setHotelData} errors={errors} />
        )}
        {step === 3 && (
          <HotelFormStep1B hotelData={hotelData} setHotelData={setHotelData} errors={errors} />
        )}
        {step === 4 && (
          <HotelFormStep2 hotelData={hotelData} setHotelData={setHotelData} errors={errors} />
        )}
        {step === 5 && (
          <HotelFormStep5Registration
            userData={userData}
            setUserData={setUserData}
            errors={errors}
          />
        )}

        <div className="form-navigation">
          {step > 1 && (
            <button type="button" className="btn btn-secondary" onClick={handlePreviousStep}>
              Back
            </button>
          )}
          {step < 5 && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleNextStep}
              style={{ display: step === 1 && !canProceedStep1 ? "none" : "inline-block" }}
            >
              Next
            </button>
          )}
          {step === 5 && (
            <button className="btn btn-success" onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? "Submitting..." : "Submit"}
            </button>
          )}
        </div>

        {message && <p className="success-message">{message}</p>}
        {error && <p className="error-message">{error}</p>}
      </div>

      {/* Bottom logo + copyright */}
      <footer className="wizard-footer" role="contentinfo">
        {/* tiny logo just before the copyright */}
        <img src="/images/logo.png" alt="Nnetego" width="20" height="20" />
        <span>
          © {new Date().getFullYear()} Nnetego. All rights reserved.{" "}
          <a href="/privacy">Privacy</a> · <a href="/terms">Terms</a>
        </span>
      </footer>
    </div>
  );
}
